import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import type { WebSocketMessage } from '@famecreator/shared-types/websocket';
import { WebSocketMessageProcessor, WebSocketService } from '@famecreator/websocket/core';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { websocketAuthAdapter } from '../adapters/websocket-auth-adapter';
import { websocketLoggerAdapter } from '../adapters/websocket-logger-adapter';
import { WebSocketContext, type WebSocketContextValue } from './websocket-context';

type MessageHandler = (message: WebSocketMessage) => void;

type WebSocketProviderProps = {
  children: ReactNode;
};

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  // Check if user is authenticated
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Initialize WebSocket service
  const serviceRef = useRef<WebSocketService | null>(null);
  const messageProcessorRef = useRef<WebSocketMessageProcessor | null>(null);

  if (!serviceRef.current) {
    const config = {
      baseUrl: import.meta.env.VITE_WS_URL || 'ws://localhost:8082',
      authProvider: websocketAuthAdapter,
      logger: websocketLoggerAdapter,
    };
    serviceRef.current = new WebSocketService(config);
    messageProcessorRef.current = new WebSocketMessageProcessor(websocketLoggerAdapter);
  }

  // Track connection state
  const [connectionState, setConnectionState] = useState(serviceRef.current.getConnectionState());
  const [processingCount, setProcessingCount] = useState(0);

  // Keep track of message subscribers
  const messageHandlers = useRef<Map<string, MessageHandler>>(new Map());
  const serviceSubscriptionId = useRef<string>('websocket-provider');

  // Handle messages from the WebSocket service
  const handleServiceMessage = useCallback((rawMessage: unknown) => {
    try {
      // Process the message to ensure proper typing
      const processedMessage = messageProcessorRef.current?.processMessage(
        JSON.stringify(rawMessage),
      );

      if (!processedMessage) {
        websocketLoggerAdapter.warn('Failed to process WebSocket message', { rawMessage });
        return;
      }

      // Update processing count for look-like inferences
      if (WebSocketMessageProcessor.isLookLikeMessage(processedMessage)) {
        const inference = processedMessage.data;
        if (inference.status === 'processing') {
          setProcessingCount((prev) => prev + 1);
        } else if (inference.status === 'completed' || inference.status === 'failed') {
          setProcessingCount((prev) => Math.max(0, prev - 1));
        }
      }

      // Distribute message to all subscribers
      messageHandlers.current.forEach((handler, id) => {
        try {
          handler(processedMessage as WebSocketMessage);
        } catch (error) {
          websocketLoggerAdapter.error(`Error in message handler for subscriber: ${id}`, {
            error,
            message: processedMessage,
          });
        }
      });
    } catch (error) {
      websocketLoggerAdapter.error('Error handling WebSocket service message', {
        error,
        rawMessage,
      });
    }
  }, []);

  // Setup WebSocket service when authenticated
  useEffect(() => {
    const service = serviceRef.current;
    if (!service) return;

    if (isAuthenticated) {
      // Subscribe to messages from the service
      const unsubscribe = service.subscribe(serviceSubscriptionId.current, handleServiceMessage);

      // Enable the WebSocket service
      service.enable();

      // Update connection state periodically
      const stateInterval = setInterval(() => {
        setConnectionState(service.getConnectionState());
      }, 1000);

      return () => {
        unsubscribe();
        clearInterval(stateInterval);
      };
    } else {
      // Disable WebSocket when not authenticated
      service.disable();
      setConnectionState('disconnected');
      setProcessingCount(0);
    }
  }, [isAuthenticated, handleServiceMessage]);

  // Methods for managing message subscriptions
  const subscribeToMessages = useCallback((id: string, handler: MessageHandler) => {
    messageHandlers.current.set(id, handler);
    websocketLoggerAdapter.debug(`Message subscriber added: ${id}`, {
      subscriberCount: messageHandlers.current.size,
    });
  }, []);

  const unsubscribeFromMessages = useCallback((id: string) => {
    const removed = messageHandlers.current.delete(id);
    if (removed) {
      websocketLoggerAdapter.debug(`Message subscriber removed: ${id}`, {
        subscriberCount: messageHandlers.current.size,
      });
    }
  }, []);

  const contextValue: WebSocketContextValue = {
    isConnected: serviceRef.current?.isConnected() ?? false,
    connectionState,
    processingCount,
    subscribeToMessages,
    unsubscribeFromMessages,
  };

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
}
